import { useRouteMatch } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useSpaceParams } from "@circle-react/components/Events/hooks/useSpaceParams";
import { getVisibleTabsArray } from "@circle-react/components/Spaces/Edit/EditForm/helpers.js";
import { isTabEnabled } from "@circle-react/helpers/spaceHelpers";
import { posts } from "@circle-react/helpers/urlHelpers";
import { useIsIframeEmbed } from "@circle-react/hooks/useIsIframeEmbed";
import type { Space } from "@circle-react/types";
import { NavigationMenu } from "../shared/NavigationMenu";

interface EventsStatusFilterProps {
  space: Space;
}

export const EventsStatusFilter = ({ space }: EventsStatusFilterProps) => {
  const { pastEvents, status, topics } = useSpaceParams();
  const visibleTabs = getVisibleTabsArray({
    visible_tabs: space?.visible_tabs || {},
  });
  const isIframeEmbed = useIsIframeEmbed();

  const isInMembersView = useRouteMatch("/c/:spaceSlug/members");
  const isInPastEventsView = !!pastEvents;
  const isInDraftEventsView = status === "draft";
  const isInUpcomingView =
    !isInMembersView && !isInPastEventsView && !isInDraftEventsView;

  const hasUpcomingTab = isTabEnabled(space, "upcoming");
  const hasPastEventsTab = isTabEnabled(space, "past");
  const hasDraftEventsTab = space.has_drafts;
  const hasOnlyOneTab = visibleTabs.length === 1 && !hasDraftEventsTab;

  const isAnyTabEnabled =
    hasUpcomingTab || hasPastEventsTab || hasDraftEventsTab;

  const generateTabsPath = (params: any) =>
    posts.index({
      spaceSlug: space.slug,
      params: {
        ...params,
        ...(topics ? { topics } : {}),
        ...(isIframeEmbed ? { iframe: true } : {}),
      },
    });

  if (hasOnlyOneTab && !isAnyTabEnabled) {
    return null;
  }

  const menuItems = [
    {
      label: t("events.upcoming"),
      active: isInUpcomingView,
      linkTo: generateTabsPath({ sort: "asc" }),
      hidden: !hasUpcomingTab,
    },
    {
      label: t("events.past"),
      active: isInPastEventsView,
      linkTo: generateTabsPath({ pastEvents: true, sort: "desc" }),
      hidden: !hasPastEventsTab,
    },
    {
      label: t("events.drafts"),
      active: isInDraftEventsView,
      linkTo: generateTabsPath({ status: "draft", sort: "asc" }),
      hidden: !hasDraftEventsTab,
    },
  ];

  const activeItem = menuItems.find(item => item.active);

  return (
    <NavigationMenu label={activeItem?.label}>
      {menuItems
        .filter(item => !item.hidden)
        .map(item => (
          <NavigationMenu.Link
            key={item.label}
            label={item.label}
            linkTo={item.linkTo}
          />
        ))}
    </NavigationMenu>
  );
};
